import { SbbOption } from "@sbb-esta/lyne-react/option";
import { SbbSelect } from "@sbb-esta/lyne-react/select";
import { FormField } from "components/Forms/FormField";
import { defaultSize } from "components/Lyne/LyneConstants";
import { LyneFormField } from "components/Lyne/LyneFormField";
import { createLogger } from "logging";
import { ChoiceModel, getChoice } from "models/ChoiceModel";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { HookFormError } from "./HookFormError";

const logger = createLogger("HookForm:Select");

interface HookFormSelectProps<
  TFieldValues extends FieldValues,
  TField extends ChoiceModel,
  TName extends Path<TFieldValues>,
> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  options: TField[];
  size?: "s" | "m" | "l";
}

export const HookFormSelect = <
  TFieldValues extends FieldValues,
  TField extends ChoiceModel,
  TName extends Path<TFieldValues>,
>(
  props: HookFormSelectProps<TFieldValues, TField, TName>
) => {
  const { name, control, options, size = defaultSize, contextHelp, label, required } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, fieldState }) => {
        const { error } = fieldState;
        const { onChange, value, ref, disabled } = field;

        // current value is shown as placeholder in lyne.
        const selectedOption = getChoice(name, options, value) as ChoiceModel;
        const selectedValue = `${selectedOption?.value}`;
        const selectedLabel = selectedOption?.text;
        logger.debug(`${name}: selected:=`, selectedOption);

        return (
          <LyneFormField
            size={size}
            name={name}
            contextHelp={contextHelp}
            required={required}
            label={label}
            key={selectedValue} /* force re-render when value changes */
          >
            <SbbSelect required={required} placeholder={selectedLabel} ref={ref} name={name} disabled={disabled}>
              {options.map((option: TField) => {
                const optionValue = `${option.value}`;
                const isSelected = optionValue === selectedValue;
                const key = `${name}-${option.text}`;
                logger.debug(`${name}: Option:`, key, optionValue, selectedValue, isSelected);

                return (
                  <SbbOption key={key} value={optionValue} selected={isSelected} onClick={() => onChange(option.value)}>
                    {option.text}
                  </SbbOption>
                );
              })}
            </SbbSelect>
            <HookFormError error={error} />
          </LyneFormField>
        );
      }}
    />
  );
};
